<template>
  <div class="app-container reels-page">
    <h1>Rolki (Reels)</h1>

    <el-card
      v-loading="settingsLoading"
      class="reels-page__card"
      shadow="never"
      data-testid="reels-settings-card"
    >
      <div slot="header" class="reels-page__card-header">
        <span>Ustawienia promocji</span>
        <el-tag v-if="settingsLoaded" :type="promoActive ? 'success' : 'info'" size="small">
          {{ promoActive ? 'Rabat na pierwszy pakiet: aktywny' : 'Rabat na pierwszy pakiet: wyłączony' }}
        </el-tag>
      </div>
      <el-form label-width="260px" size="small" @submit.native.prevent>
        <el-form-item label="Darmowe rolki miesięcznie">
          <el-input-number
            v-model="settingsForm.monthly_free_limit"
            :min="0"
            :max="100"
            data-testid="reels-settings-monthly"
          />
          <span class="field-hint">
            {{ originalSettings && originalSettings.monthly_free_limit === null ? 'dziś bez limitu (brak ustawienia); ' : '' }}dla każdego użytkownika, odnawiane 1. dnia miesiąca
          </span>
        </el-form-item>
        <el-form-item label="Rabat na pierwszy pakiet rolek (%)">
          <el-input-number
            v-model="settingsForm.first_purchase_discount_percent"
            :min="0"
            :max="90"
            data-testid="reels-settings-percent"
          />
          <span class="field-hint">0 = promocja wyłączona; dla kont bez żadnego opłaconego zakupu rolek</span>
        </el-form-item>
        <el-form-item label="Rabat na pierwszy pakiet ważny do">
          <el-date-picker
            v-model="settingsForm.first_purchase_discount_until"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd.MM.yyyy"
            placeholder="bezterminowo"
            clearable
            data-testid="reels-settings-until"
          />
          <span class="field-hint">włącznie z tym dniem; puste = bez daty końca</span>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="settingsSaving"
            data-testid="reels-settings-save"
            @click="saveSettings"
          >
            Zapisz ustawienia
          </el-button>
          <el-button :disabled="settingsSaving" @click="resetSettingsForm">Przywróć</el-button>
        </el-form-item>
      </el-form>
      <div class="cell-muted">
        Rabaty się nie sumują - przy zakupie wygrywa najwyższy (promocja pierwszego pakietu albo rabat przypisany użytkownikowi).
        Rolki i rabaty dla konkretnych osób przydzielisz w edycji użytkownika albo zbiorczo na liście użytkowników.
      </div>
    </el-card>

    <el-card
      v-loading="overviewLoading"
      class="reels-page__card"
      shadow="never"
      data-testid="reels-active-discounts"
    >
      <div slot="header" class="reels-page__card-header">
        <span>Aktywne rabaty</span>
        <el-button
          type="text"
          icon="el-icon-refresh"
          :disabled="overviewLoading"
          @click="loadOverview"
        >
          Odśwież
        </el-button>
      </div>
      <el-table
        :data="overview.active_discounts"
        border
        size="small"
        empty-text="Brak aktywnych rabatów"
      >
        <el-table-column label="Użytkownik" min-width="220">
          <template slot-scope="scope">
            <router-link :to="userEditLink(scope.row.user)" class="user-link">
              {{ scope.row.user.name || scope.row.user.email || scope.row.user.id }}
            </router-link>
            <div v-if="scope.row.user.name && scope.row.user.email" class="cell-muted">{{ scope.row.user.email }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Rabat" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.percent }}%</template>
        </el-table-column>
        <el-table-column label="Użycia" width="130" align="center">
          <template slot-scope="scope">{{ formatDiscountUses(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="Ważny do" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.expires_at ? formatDate(scope.row.expires_at) : 'bezterminowo' }}</template>
        </el-table-column>
        <el-table-column label="Notatka" min-width="160">
          <template slot-scope="scope">{{ scope.row.note || '-' }}</template>
        </el-table-column>
        <el-table-column label="Dodał" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.created_by ? scope.row.created_by.name : '-' }}
            <div class="cell-muted">{{ formatDateTime(scope.row.created_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="" width="100" align="center">
          <template slot-scope="scope">
            <el-button
              type="danger"
              size="mini"
              :loading="revokingDiscountId === scope.row.id"
              @click="revokeDiscount(scope.row)"
            >
              Cofnij
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="cell-muted table-footnote">Maksymalnie 200 najnowszych aktywnych rabatów.</div>
    </el-card>

    <el-card
      v-loading="overviewLoading"
      class="reels-page__card"
      shadow="never"
      data-testid="reels-recent-grants"
    >
      <div slot="header" class="reels-page__card-header">
        <span>Ostatnie przydziały</span>
      </div>
      <el-table
        :data="overview.recent_grants"
        border
        size="small"
        empty-text="Brak przydziałów"
      >
        <el-table-column label="Data" width="150">
          <template slot-scope="scope">{{ formatDateTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Użytkownik" min-width="220">
          <template slot-scope="scope">
            <router-link :to="userEditLink(scope.row.user)" class="user-link">
              {{ scope.row.user.name || scope.row.user.email || scope.row.user.id }}
            </router-link>
            <div v-if="scope.row.user.name && scope.row.user.email" class="cell-muted">{{ scope.row.user.email }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Zmiana" width="80" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.delta > 0 ? 'delta-plus' : 'delta-minus'">
              {{ scope.row.delta > 0 ? '+' + scope.row.delta : scope.row.delta }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Saldo po" width="80" align="center" prop="balance_after" />
        <el-table-column label="Powód" width="170">
          <template slot-scope="scope">{{ ledgerReasonLabel(scope.row.reason) }}</template>
        </el-table-column>
        <el-table-column label="Notatka" min-width="160">
          <template slot-scope="scope">{{ scope.row.note || '-' }}</template>
        </el-table-column>
        <el-table-column label="Admin" min-width="120">
          <template slot-scope="scope">{{ scope.row.admin ? scope.row.admin.name : '-' }}</template>
        </el-table-column>
      </el-table>
      <div class="cell-muted table-footnote">Ostatnie 50 przydziałów i odjęć rolek przez admina.</div>
    </el-card>
  </div>
</template>

<script>
import {
  getReelSettings,
  updateReelSettings,
  getReelsOverview,
  revokeUserReelDiscount,
} from '@/api/reels';
import {
  ledgerReasonLabel,
  formatDate,
  formatDateTime,
  formatDiscountUses,
} from '@/utils/reels';

const SETTING_KEYS = ['monthly_free_limit', 'first_purchase_discount_percent', 'first_purchase_discount_until'];

/**
 * Strona "Rolki": globalne ustawienia (darmowe rolki / mc, promocja pierwszego pakietu),
 * aktywne rabaty użytkowników i ostatnie przydziały rolek przez admina.
 */
export default {
  name: 'ReelsPromotions',
  data() {
    return {
      settingsLoading: false,
      settingsSaving: false,
      settingsLoaded: false,
      originalSettings: null,
      settingsForm: {
        monthly_free_limit: null,
        first_purchase_discount_percent: 0,
        first_purchase_discount_until: null,
      },
      overviewLoading: false,
      overview: {
        active_discounts: [],
        recent_grants: [],
      },
      revokingDiscountId: null,
    };
  },
  computed: {
    promoActive() {
      const settings = this.originalSettings;
      if (!settings || !settings.first_purchase_discount_percent) {
        return false;
      }
      if (!settings.first_purchase_discount_until) {
        return true;
      }
      return settings.first_purchase_discount_until >= this.todayWarsaw();
    },
  },
  created() {
    this.loadSettings();
    this.loadOverview();
  },
  methods: {
    ledgerReasonLabel,
    formatDate,
    formatDateTime,
    formatDiscountUses,
    todayWarsaw() {
      // "sv-SE" daje format Y-m-d.
      return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Warsaw' });
    },
    userEditLink(user) {
      return { path: '/administrator/users/edit', query: { id: user.id }};
    },
    applySettings(settings) {
      this.originalSettings = Object.assign({}, settings);
      this.settingsForm = {
        // null (brak ustawienia i limitu w configu) = bez limitu; undefined zostawia pole puste.
        monthly_free_limit: settings.monthly_free_limit === null ? undefined : settings.monthly_free_limit,
        first_purchase_discount_percent: settings.first_purchase_discount_percent,
        first_purchase_discount_until: settings.first_purchase_discount_until || null,
      };
      this.settingsLoaded = true;
    },
    resetSettingsForm() {
      if (this.originalSettings) {
        this.applySettings(this.originalSettings);
      }
    },
    async loadSettings() {
      this.settingsLoading = true;
      try {
        this.applySettings(await getReelSettings());
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios (src/utils/request.js).
      } finally {
        this.settingsLoading = false;
      }
    },
    async loadOverview() {
      this.overviewLoading = true;
      try {
        const data = await getReelsOverview();
        this.overview = {
          active_discounts: data.active_discounts || [],
          recent_grants: data.recent_grants || [],
        };
        if (data.settings && !this.settingsLoaded) {
          this.applySettings(data.settings);
        }
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios.
      } finally {
        this.overviewLoading = false;
      }
    },
    async saveSettings() {
      const form = this.settingsForm;
      const changes = {};
      SETTING_KEYS.forEach(key => {
        let value = key === 'first_purchase_discount_until' ? (form[key] || null) : form[key];
        if (value === undefined) {
          value = null;
        }
        if (!this.originalSettings || value !== this.originalSettings[key]) {
          changes[key] = value;
        }
      });

      if ('monthly_free_limit' in changes && !this.isIntInRange(changes.monthly_free_limit, 0, 100)) {
        this.$message.warning('Darmowe rolki miesięcznie: podaj liczbę od 0 do 100.');
        return;
      }
      if ('first_purchase_discount_percent' in changes && !this.isIntInRange(changes.first_purchase_discount_percent, 0, 90)) {
        this.$message.warning('Rabat na pierwszy pakiet: podaj liczbę od 0 do 90.');
        return;
      }
      if (Object.keys(changes).length === 0) {
        this.$message.info('Brak zmian do zapisania.');
        return;
      }

      this.settingsSaving = true;
      try {
        this.applySettings(await updateReelSettings(changes));
        this.$message.success('Zapisano ustawienia rolek.');
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios.
      } finally {
        this.settingsSaving = false;
      }
    },
    isIntInRange(value, min, max) {
      return Number.isInteger(value) && value >= min && value <= max;
    },
    async revokeDiscount(discount) {
      const who = discount.user.name || discount.user.email || discount.user.id;
      try {
        await this.$confirm(
          'Cofnąć rabat ' + discount.percent + '% użytkownika ' + who + '?',
          'Potwierdzenie',
          {
            confirmButtonText: 'Cofnij rabat',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch (cancel) {
        return;
      }

      this.revokingDiscountId = discount.id;
      try {
        await revokeUserReelDiscount(discount.user.id, discount.id);
        this.$message.success('Rabat cofnięty.');
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios.
      } finally {
        this.revokingDiscountId = null;
        this.loadOverview();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.reels-page {
  &__card {
    margin-bottom: 20px;
  }
  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
  }
}
.field-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
.cell-muted {
  font-size: 12px;
  color: #909399;
}
.table-footnote {
  margin-top: 6px;
}
.user-link {
  color: #409eff;
}
.delta-plus {
  color: #67c23a;
  font-weight: 600;
}
.delta-minus {
  color: #f56c6c;
  font-weight: 600;
}
</style>
